import { defineStore } from 'pinia';
import { api, ApiBaseUrl } from '../api';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: '',
        expire_at: '',
        user: {} as Record<string, any>,
        avatar: '',
        permissions: [] as string[],
        roles: [] as string[],
        error: null as string | null,
        loading: false,
        loaded: false,
        organization: {} as Record<string, any>
    }),
    getters: {
        getRoles(state) {
            return state.roles;
        },
        getUser(state) {
            return state.user;
        },
        getPermissions(state) {
            return state.permissions;
        },
        getOrganization(state) {
            return state.organization;
        },
        getAccessToken(state) {
            return state.token;
        },
        tokenHasExpired(state) {
            if (!state.expire_at) return true;

            const now = Date.now();
            const expireAt = new Date(state.expire_at).getTime();

            return now >= expireAt;
        }
    },
    actions: {
        canAny(perm: string[]) {
            return perm.some((item) => this.can(item));
        },
        can(perm: string) {
            return this.permissions.includes(perm);
        },
        setToken(token: string, expire_at: string) {
            this.token = token;
            this.expire_at = expire_at;
        },

        setUser(user: Record<string, any>) {
            this.user = user;
        },
        setRoles(roles: []) {
            this.roles = roles;
        },
        setPermissions(perms: []) {
            this.permissions = perms;
        },
        setOrganization(organization: Record<string, any>) {
            if (!organization) {
                this.organization = {};
                return;
            }
            const { settings, ...rest } = organization;
            this.organization = rest;
        },
        async switchOrganisation(organisation: Record<string, any>) {
            await api.post(`/organisations/${organisation.id}/switch`);
        },
        async fetchAuthUser() {
            this.loading = true;
            this.error = null;

            try {
                await api.get('/sanctum/csrf-cookie', { baseURL: ApiBaseUrl });
                const response = await api.get('user');
                this.setUser(response.data?.user);
                this.setRoles(response.data?.roles);
                this.setPermissions(response.data?.permissions);
                this.setOrganization(response.data?.organization);
                return response.data;
            } catch (error: any) {
                this.error = error.response?.data || 'Error fetching user';
                throw error;
            } finally {
                this.loading = false;
                this.loaded = true;
            }
        },
        async logout() {
            this.loading = true;
            this.error = null;

            try {
                const response = await api.post('logout');
                return response.data;
            } catch (error: any) {
                this.error = error.response?.data || 'Error fetching user';
                throw error;
            } finally {
                this.loading = false;
            }
        },
        resetState() {
            this.token = '';
            this.expire_at = '';
            this.user = {};
            this.avatar = '';
            this.permissions = [];
            this.roles = [];
            this.organization = {};
            this.error = null;
            this.loading = false;
        },
        clearState() {
            const storageKey = import.meta.env.VITE_APP_WEBSTORAGE_AUTH_NAMESPACE || 'caretaker';
            localStorage.removeItem(storageKey);
            localStorage.removeItem('auth');
            this.resetState();
            this.$reset();
        }
    }
});